/**
 * Robot Link Protocol v1 — the wire contract between the onboard Platform
 * Connection Agent and the Robot Communication Service.
 *
 * Transport: a single robot-initiated WebSocket over TLS (wss://<host>/robot/v1/link)
 * carrying JSON text frames. Each frame is one Envelope. The envelope is
 * transport-neutral so the link can later move to MQTT without changing bodies.
 *
 * Normative semantics (also documented in docs/onboard-requirements.md):
 *  - Every platform→robot `cmd` carries a platform-generated `commandId` (UUID).
 *    The robot MUST treat a repeated commandId as a duplicate: never re-execute;
 *    re-send the last known ack/result instead.
 *  - `ttlMs` is measured from the robot's RECEIPT time (no cross-clock trust).
 *    A command not started within ttlMs MUST be reported as `expired`.
 *  - On (re)connect the robot sends `hello` listing active commands and commands
 *    that reached a terminal state within the retention window (>= 24h). The
 *    platform reconciles before dispatching anything new.
 *  - The robot only reports a terminal outcome it has evidence for. When it cannot
 *    establish one (agent restarted mid-execution, deadline passed without the
 *    executor confirming the stop, service reply lost) it reports `unknown`, and
 *    reports the real outcome later if it learns it.
 *  - Teleop frames are never queued, persisted or replayed.
 *
 * Authority (architecture v0.3 §7.5 — proposed, to validate with the onboard team):
 *  - The platform issues a monotonically increasing per-robot `authorityGeneration`,
 *    bumped on every control grant, takeover, release, expiry and mode change, and
 *    installs it with `authority_install`. The robot persists the installed generation
 *    durably BEFORE answering `authority_ack`, reports it in `hello.authority`, and
 *    never lowers it.
 *  - Every `cmd` carries `authorityGeneration` and `mode`. Admission order on the robot:
 *    (1) dedupe by commandId (a duplicate returns the recorded status and is never
 *    re-checked against authority); (2) e-stop ENGAGE (`safety.estop`) is admitted
 *    regardless of generation; (3) otherwise reject `AUTH.STALE_GENERATION` if the
 *    generation is below the installed one, `AUTH.NOT_INSTALLED` if above it (or none is
 *    installed yet), and `AUTH.MODE_NOT_PERMITTED` unless MODES_PERMITTED[installed mode]
 *    contains `mode`. Teleop frames must carry the installed generation under a `teleop`
 *    installation; others are dropped. `cmd_cancel` is always honoured (stopping is never
 *    gated). Installing a generation does not stop admitted work (disposition `continue`).
 *  - The platform dispatches a command only while the robot's acknowledged generation
 *    equals both the platform's current generation and the command's generation.
 *    Never-sent commands from a superseded generation are cancelled (`AUTH.SUPERSEDED`).
 *
 * Payload identity (§7.6): `cmd.payloadSha256` is an opaque platform-computed identity
 * of (op, interfaceId, args, missionId). The robot stores it with the command; a
 * repeated commandId with a DIFFERENT hash is rejected (`CMD.PAYLOAD_MISMATCH`) without
 * touching the recorded command.
 *
 * Ordering (§7.6): every robot→platform envelope carries `seq`, strictly increasing per
 * link connection and starting at 0 with `hello`. The platform drops frames whose seq
 * does not increase, and frames from a connection that has been superseded.
 * `hello.robotBootId` (kernel boot id) distinguishes robot reboots from agent restarts
 * (`hello.bootId`, per agent process).
 *
 * Lifecycle milestones (§7.7): `cmd_event` reports cancel_accepted / cancel_rejected
 * (replacing the cancel-refusal marker in cmd_feedback, still accepted for compatibility)
 * and deadline_breached (the robot's own execution deadline passed; its stop policy is
 * running — NOT an outcome). Cancellation completes only with a `cancelled` (or, after a
 * deadline, `timed_out`) cmd_result from the executor.
 *
 * Configuration activation (§7.8 — proposed, to validate with the onboard team). A bundle
 * reaches the robot in two commands, never one:
 *  - `config_stage` (ConfigStageArgs → ConfigStageResult): download over HTTPS, size +
 *    sha256, safe extraction, manifest check, software/schema compatibility check
 *    (`manifest.compat`), copy into the robot's version store. Never touches the active
 *    configuration, so it may run while the robot works. Cancellable until the robot's
 *    stage service is called. Incompatible → failed `BUNDLE.INCOMPATIBLE`.
 *  - `config_activate` (ConfigActivateArgs → ConfigActiveReport): only for a staged
 *    version+sha (`BUNDLE.NOT_STAGED`). The robot activates only at a safe boundary: while
 *    a mission or any action goal runs it refuses with failed `CMD.BUSY` (no live-change
 *    rule is supported: every activation waits for idle) and the platform retries later.
 *    The switch is atomic and covers every consumer (map server, route planner, mission
 *    executor); if any consumer fails to load it the previous version is restored and the
 *    result is failed `BUNDLE.ACTIVATION_FAILED` — partial activation is never reported as
 *    success. Not cancellable once called. Success acknowledges the active version AND
 *    checksum, which must equal the args.
 *  - Both carry `retainVersions`: bundle versions the platform still needs on the robot
 *    (pinned by unfinished missions, plus the active one for rollback). The robot never
 *    prunes them.
 *  - The robot's activation service is authoritative for what is active. After an agent
 *    restart the agent reconciles its cached state with it before `hello`, and resolves an
 *    activation left `unknown` with that evidence (active = requested → succeeded,
 *    otherwise failed: the switch is atomic). `hello.configVersion/configChecksum` report
 *    the reconciled active bundle.
 *  - `RunWorkflow` goals carry `bundle_version` and `bundle_sha256`; the robot refuses a
 *    goal pinned to anything but the active bundle (`mission.config_mismatch`).
 */
import { z } from "zod";
import { CommandMode, ControlMode, OperatingState, Pose2D, RobotReportedCommandStatus } from "./common.js";
import { RegistrySnapshot } from "./registry.js";
export const ROBOT_LINK_PROTOCOL_VERSION = 1;
export const ROBOT_LINK_SUBPROTOCOL = "tcr.robot-link.v1";
/** Structured error carried in rejections and failures. `code` comes from ERROR_CODES or the robot's own catalog. */
export const LinkError = z.object({
    code: z.string().min(1).max(128),
    message: z.string().max(2000).default(""),
    details: z.unknown().optional(),
});
/** Snapshot of a command the robot knows about (used in hello for reconciliation). */
export const CommandReport = z.object({
    commandId: z.string().uuid(),
    status: RobotReportedCommandStatus,
    /** Robot epoch-ms of the last status change. */
    updatedAt: z.number().int(),
    result: z.unknown().optional(),
    error: LinkError.optional(),
});
/** One active robot error. `code` is the catalog key; `instanceId` disambiguates repeated occurrences. */
export const RobotErrorReport = z.object({
    code: z.string().min(1).max(128),
    instanceId: z.string().min(1).max(128),
    /** Robot-side priority 0..100 if available (higher = more urgent). */
    priority: z.number().min(0).max(100).optional(),
    message: z.string().max(2000).optional(),
    /** Robot epoch-ms when raised. */
    stamp: z.number().int(),
});
/** Authority generation installed on the robot (0 = none installed yet). */
export const AuthorityState = z.object({
    generation: z.number().int().nonnegative(),
    mode: ControlMode,
    leaseId: z.string().uuid().nullable(),
});
// ───────────────────────────── robot → platform ─────────────────────────────
export const HelloBody = z.object({
    robotId: z.string().uuid(),
    /** Changes on every agent process start; lets the platform detect reboots/restarts. */
    bootId: z.string().min(1).max(64),
    /** Kernel boot id (/proc/sys/kernel/random/boot_id): changes only when the robot computer reboots. */
    robotBootId: z.string().max(64).nullable().default(null),
    /** Authority generation the robot has installed (persisted across restarts). */
    authority: AuthorityState.default({ generation: 0, mode: "operate", leaseId: null }),
    protocolVersion: z.literal(ROBOT_LINK_PROTOCOL_VERSION),
    agentVersion: z.string().max(64),
    softwareVersion: z.string().max(128).default("unknown"),
    /** Currently applied configuration bundle, if any. */
    configVersion: z.number().int().nullable(),
    configChecksum: z.string().nullable(),
    /** Hash of the registry snapshot the agent last applied (null on first boot). */
    registryHash: z.string().nullable(),
    /** Interface ids the robot's LOCAL allowlist permits. The platform never sends others. */
    localAllowlist: z.array(z.string()),
    /** Free-form capability flags, e.g. "teleop.swerve", "calibration.lidar", "mission.workflow.v1". */
    capabilities: z.array(z.string()),
    /** Active and recently-terminal commands, for reconciliation. */
    commands: z.array(CommandReport),
});
/** StateBody.mapFingerprint (v1, grid only) and .mapFingerprintV2 formats; see MAP_FINGERPRINT_VERSION / _V2_VERSION. */
export const MAP_FINGERPRINT_V1_PATTERN = /^[0-9a-f]{64}$/;
export const MAP_FINGERPRINT_V2_PATTERN = /^tcr-grid-v2:[0-9a-f]{64}$/;
export const StateBody = z.object({
    operatingState: OperatingState,
    /** Raw onboard state-machine name for diagnostics (e.g. "NAVIGATE", "TELEOP"). */
    rawState: z.string().max(128).default(""),
    battery: z
        .object({
        soc: z.number().min(0).max(100),
        charging: z.boolean(),
        voltage: z.number().optional(),
    })
        .nullable(),
    pose: Pose2D.nullable(),
    /** Platform map id the robot is localised on, if known (from the active config bundle). */
    mapId: z.string().uuid().nullable().default(null),
    /**
     * Grid-only (tcr-grid-v1, MAP_FINGERPRINT_VERSION) fingerprint of the occupancy grid the
     * robot's map server publishes now (latched /map): 64 lowercase hex, no prefix. It does NOT
     * cover resolution or origin, so on its own it never identifies a map (a robot that sends
     * only this is a legacy agent: RobotDto.reported.mapUnidentified "unverified"). Agents that
     * send mapFingerprintV2 keep sending this too: it is the platform's lookup key, and older
     * platforms only read this field.
     */
    mapFingerprint: z.string().regex(MAP_FINGERPRINT_V1_PATTERN).nullable().default(null),
    /**
     * Geometry-verified fingerprint of the same grid (MAP_FINGERPRINT_V2_VERSION): covers the
     * cells (via mapFingerprint) plus canonical resolution and origin x/y/yaw. Lets the platform
     * identify the map when no platform bundle is active (a map imported from the robot, or
     * uploaded from the robot's own yaml + pgm) without confusing grids that are identical cell
     * for cell but placed or scaled differently. Only meaningful together with mapFingerprint.
     */
    mapFingerprintV2: z.string().regex(MAP_FINGERPRINT_V2_PATTERN).nullable().default(null),
    localized: z.boolean().default(false),
    activeCommandId: z.string().uuid().nullable().default(null),
    /** 0..1 progress of the active mission if the executor reports it. */
    missionProgress: z.number().min(0).max(1).nullable().default(null),
    estop: z.object({ hardware: z.boolean(), software: z.boolean() }),
    errors: z.array(RobotErrorReport).max(200),
    configVersion: z.number().int().nullable().default(null),
    teleopActive: z.boolean().default(false),
});
export const TelemetrySample = z.object({
    interfaceId: z.string(),
    /** Robot epoch-ms of the source message (header stamp if present, else receipt). */
    stamp: z.number().int(),
    data: z.unknown(),
});
export const TelemetryBody = z.object({ samples: z.array(TelemetrySample).min(1).max(500) });
export const CmdAckBody = z.object({
    commandId: z.string().uuid(),
    accepted: z.boolean(),
    /** True when the robot has seen this commandId before and did not re-execute. */
    duplicate: z.boolean().default(false),
    reason: LinkError.optional(),
});
export const CmdFeedbackBody = z.object({
    commandId: z.string().uuid(),
    /** First feedback for a command implies execution started. */
    progress: z.number().min(0).max(1).nullable().default(null),
    feedback: z.unknown().optional(),
});
export const CmdResultBody = z.object({
    commandId: z.string().uuid(),
    /**
     * `unknown`: the robot cannot establish the outcome (e.g. a deadline passed and the
     * executor never confirmed the stop). Not terminal — a later cmd_result with the
     * real outcome supersedes it.
     */
    status: z.enum(["succeeded", "failed", "cancelled", "timed_out", "expired", "unknown"]),
    result: z.unknown().optional(),
    error: LinkError.optional(),
});
/**
 * Lifecycle milestone that is not an outcome (v0.3 §7.7).
 *  - cancel_accepted: the executor accepted the cancel request (completion comes as cmd_result);
 *  - cancel_rejected: the executor refused to cancel; the command keeps running (`error` says why);
 *  - deadline_breached: the robot's execution deadline passed and its stop policy started.
 */
export const CmdEventBody = z.object({
    commandId: z.string().uuid(),
    event: z.enum(["cancel_accepted", "cancel_rejected", "deadline_breached"]),
    /** Robot epoch-ms when it happened. */
    at: z.number().int(),
    error: LinkError.optional(),
    detail: z.unknown().optional(),
});
/** Answer to `authority_install`. Sent only after the generation is durably persisted. */
export const AuthorityAckBody = z.object({
    generation: z.number().int().positive(),
    /** False when `generation` is below what the robot already has installed. */
    accepted: z.boolean(),
    /** What the robot has installed after handling the request. */
    installedGeneration: z.number().int().nonnegative(),
    /** Commands still executing when the generation was installed (disposition `continue`: they keep running). */
    activeCommandIds: z.array(z.string().uuid()),
    reason: LinkError.optional(),
});
/** Which registry entries the agent actually bound (missing types, local-allowlist misses, ...). */
export const BindReportBody = z.object({
    registryHash: z.string(),
    bound: z.array(z.string()),
    failed: z.array(z.object({ interfaceId: z.string(), error: LinkError })),
});
/** Motion group of a teleop stream: the swerve base (`teleop`) or the arm (`arm_jog`). One moves at a time. */
export const TeleopGroup = z.enum(["base", "arm"]);
/** Robot-side teleop watchdog events (e.g. stopped because frames stopped arriving). */
export const TeleopStatusBody = z.object({
    leaseId: z.string().uuid(),
    /** Which stream this is about; absent = `base` (robots before arm jog never send it). */
    group: TeleopGroup.optional(),
    active: z.boolean(),
    lastSeq: z.number().int().nonnegative(),
    /**
     * group_busy: frames of one group are dropped while the other moves (one moving part at a time).
     * singularity / joint_limit / collision: arm jog halted by Servo (ArmState.jog_status), group `arm` only.
     */
    stoppedReason: z
        .enum(["watchdog", "lease_ended", "estop", "mode_change", "platform_stop", "group_busy", "singularity", "joint_limit", "collision"])
        .optional(),
});
export const PongBody = z.object({ nonce: z.string() });
export const AuthRefreshBody = z.object({ token: z.string().min(10) });
// ───────────────────────────── platform → robot ─────────────────────────────
export const WelcomeBody = z.object({
    sessionId: z.string().uuid(),
    serverTime: z.number().int(),
    heartbeatIntervalMs: z.number().int().positive(),
    /** Robot must re-authenticate (auth_refresh) before this epoch-ms or be disconnected. */
    authExpiresAt: z.number().int(),
    registry: RegistrySnapshot,
});
/**
 * Generic operation dispatched to a registry entry, or a built-in configuration step.
 *  - service_call: args = request fields
 *  - action_goal: args = goal fields (feedback via cmd_feedback, result via cmd_result)
 *  - publish:     args = message fields (ack then immediate `succeeded` result)
 *  - config_stage:    args = ConfigStageArgs; agent downloads + verifies, then hands the
 *                     bundle to `config.stage_bundle`. Result = ConfigStageResult.
 *  - config_activate: args = ConfigActivateArgs; `config.activate_bundle` switches to the
 *                     staged version. Result = ConfigActiveReport.
 *  - map_export:      args = MapExportArgs; the agent uploads the robot's current map
 *                     (see MapExportArgs). Result = MapExportResult.
 */
export const CmdOp = z.enum(["service_call", "action_goal", "publish", "config_stage", "config_activate", "map_export"]);
/** Capability a robot reports in `hello` when it implements the activation contract. */
export const CONFIG_ACTIVATION_CAPABILITY = "config.activation.v1";
/**
 * Map import from the robot ("Import map from robot"). `map_export` is a built-in agent
 * op (interfaceId WELL_KNOWN_INTERFACES.exportMap, mode `observe`; must be in the robot's
 * local allowlist; no registry entry). The agent subscribes to `topic`
 * (nav_msgs/OccupancyGrid, transient_local — map_server latches it), waits up to
 * `waitSec` for a message, encodes it as a ROS map pair:
 *   image: trinary P5 PGM, rows flipped (PGM row 0 = top = highest grid row); cell -1 ->
 *          205, cell >= 65 -> 0 (occupied), cell <= 19 -> 254 (free), otherwise 205;
 *   yaml:  image, mode trinary, resolution, origin [x, y, yaw], negate 0,
 *          occupied_thresh 0.65, free_thresh 0.196;
 * and POSTs multipart/form-data (`yaml` file `map.yaml`, `image` file `map.pgm`) to
 * `uploadUrl` with its robot bearer token. `uploadUrl` must be on the agent's
 * platform_url origin (same rule as bundle downloads). The platform creates the map (or a
 * new version of it) and answers MapExportResult, which the agent returns as the command
 * result. Errors: MAP.UNAVAILABLE, MAP.TOO_LARGE, MAP.UPLOAD_FAILED (+ the platform's
 * error in `details`). Robots that implement it report MAP_EXPORT_CAPABILITY in hello.
 */
export const MAP_EXPORT_CAPABILITY = "map.export.v1";
/**
 * Occupancy-grid fingerprint v1 (StateBody.mapFingerprint), normative for robot and platform:
 *
 *   sha256 over  "tcr-grid-v1\n" + width + "\n" + height + "\n"  (ASCII decimal)
 *                followed by the width × height cells as signed int8 in nav_msgs/OccupancyGrid
 *                order (row 0 = bottom row of the image, i.e. lowest y; values -1, 0..100)
 *
 * reported as 64 lowercase hex characters, no prefix. The robot hashes its latched /map. The
 * platform derives the same cells from a stored map version the way Nav2's map_server loads it
 * (nav2_map_server map_io, see `occupancyCellsFromImage`), so a version uploaded from the
 * robot's yaml + pgm matches as well as one imported from the robot. Resolution and origin are
 * NOT hashed: v1 alone never identifies a map (see MAP_FINGERPRINT_V2_VERSION).
 */
export const MAP_FINGERPRINT_VERSION = "tcr-grid-v1";
/** Bytes to sha256 for a map fingerprint (see MAP_FINGERPRINT_VERSION). */
export function mapFingerprintPayload(width, height, cells) {
    if (cells.length !== width * height)
        throw new Error("cells length must be width × height");
    const head = new TextEncoder().encode(`${MAP_FINGERPRINT_VERSION}\n${width}\n${height}\n`);
    const out = new Uint8Array(head.length + cells.length);
    out.set(head, 0);
    out.set(new Uint8Array(cells.buffer, cells.byteOffset, cells.length), head.length);
    return out;
}
/**
 * Geometry-verified occupancy-grid fingerprint v2 (StateBody.mapFingerprintV2), normative for
 * robot (Python agent state.py, mock robot) and platform:
 *
 *   "tcr-grid-v2:" + sha256 hex (lowercase) of the ASCII text
 *
 *     tcr-grid-v2\n
 *     grid=<v1>\n                  the tcr-grid-v1 fingerprint (64 lowercase hex) of the grid
 *     resolution_um=<Q(f32(resolution))>\n
 *     origin_x_um=<Q(origin.x)>\n
 *     origin_y_um=<Q(origin.y)>\n
 *     origin_yaw_urad=<Y(yaw)>\n
 *
 * Canonical numbers (each step is a single IEEE-754 operation, identical in JS and Python):
 *   - f32(v): v rounded to the nearest binary32 (Math.fround / struct "<f" round trip).
 *     OccupancyGrid.info.resolution is float32 on the robot; the platform rounds the yaml
 *     value the same way, so both hash the same number.
 *   - Q(v) = floor(v * 1e6 + 0.5): micrometres / microradians, ties toward +∞ (explicit;
 *     NOT the language's round()/toFixed, which differ on ties). Printed as a plain base-10
 *     integer: optional "-", no "+", no leading zeros, no exponent; zero is always "0" (no -0).
 *     |Q| must be ≤ 2^53 - 1.
 *   - yaw: robot: atan2(2(w·z + x·y), 1 − 2(y² + z²)) of info.origin.orientation (identity
 *     when absent); platform: the yaml origin[2] (map_server builds the quaternion from it).
 *   - Y(yaw) = Q(yaw − 2π·floor((yaw + π) / 2π)) with π = the binary64 nearest π, and then
 *     −3141593 → 3141593 (+π and −π are one angle).
 *   - resolution must be finite and > 0, origin values finite; otherwise there is no v2
 *     fingerprint (null).
 *
 * Grids whose cells are identical but whose resolution or origin differ by ≥ ~1 µm / 1 µrad get
 * different v2 fingerprints. A value lying (within floating-point noise, e.g. the quaternion
 * round trip of the yaw) exactly on a quantisation boundary could still round differently on
 * the two sides: such a robot is reported unidentified (geometry_mismatch), never as a wrong
 * map. Golden vectors: packages/contracts/fixtures/map-fingerprint-golden.json (copied verbatim
 * to tcr_platform_agent/test/fixtures/map_fingerprint_golden.json; contract and agent tests
 * check them).
 */
export const MAP_FINGERPRINT_V2_VERSION = "tcr-grid-v2";
const YAW_PI_URAD = 3141593;
function quantizeMicro(v) {
    if (!Number.isFinite(v))
        return null;
    const q = Math.floor(v * 1e6 + 0.5);
    if (!Number.isSafeInteger(q))
        return null;
    return q === 0 ? 0 : q; // -0 → 0
}
/** Yaw (rad) of a geometry_msgs/Quaternion, as MAP_FINGERPRINT_V2_VERSION defines it. */
export function quaternionYaw(q) {
    const x = q?.x ?? 0;
    const y = q?.y ?? 0;
    const z = q?.z ?? 0;
    const w = q?.w ?? 1;
    return Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
}
/** Canonical integers of MAP_FINGERPRINT_V2_VERSION, or null when the geometry is invalid. */
export function canonicalMapGeometry(g) {
    if (!Number.isFinite(g.resolution) || !(g.resolution > 0) || !Number.isFinite(g.yaw))
        return null;
    const twoPi = 2 * Math.PI;
    const resolutionUm = quantizeMicro(Math.fround(g.resolution));
    const originXUm = quantizeMicro(g.originX);
    const originYUm = quantizeMicro(g.originY);
    let originYawUrad = quantizeMicro(g.yaw - twoPi * Math.floor((g.yaw + Math.PI) / twoPi));
    if (resolutionUm === null || originXUm === null || originYUm === null || originYawUrad === null)
        return null;
    if (originYawUrad === -YAW_PI_URAD)
        originYawUrad = YAW_PI_URAD;
    return { resolutionUm, originXUm, originYUm, originYawUrad };
}
/** ASCII text to sha256 for a v2 fingerprint (see MAP_FINGERPRINT_V2_VERSION); null for invalid input. */
export function mapFingerprintV2Payload(gridFingerprint, g) {
    if (!MAP_FINGERPRINT_V1_PATTERN.test(gridFingerprint))
        return null;
    const c = canonicalMapGeometry(g);
    if (!c)
        return null;
    return (`${MAP_FINGERPRINT_V2_VERSION}\ngrid=${gridFingerprint}\nresolution_um=${c.resolutionUm}\n` +
        `origin_x_um=${c.originXUm}\norigin_y_um=${c.originYUm}\norigin_yaw_urad=${c.originYawUrad}\n`);
}
/** The reported v2 fingerprint string for the sha256 hex digest of its payload. */
export function mapFingerprintV2FromDigest(sha256Hex) {
    return `${MAP_FINGERPRINT_V2_VERSION}:${sha256Hex}`;
}
/**
 * OccupancyGrid cells exactly as Nav2 map_server would publish them for a greyscale image
 * (8-bit, row 0 = top) with the map yaml parameters. Mirrors nav2_map_server map_io.cpp:
 * shade = pixel / 255; occ = negate ? shade : 1 - shade;
 *   trinary: occupied_thresh < occ → 100; occ < free_thresh → 0; else -1
 *   scale:   occ < free → 0; occ ≥ occupied → 100; else round((occ - free) / (occupied - free) × 100)
 *   raw:     the pixel value itself (values > 100 become -1)
 * and the vertical flip (image row y → grid row height - 1 - y).
 */
export function occupancyCellsFromImage(img, p) {
    const { width, height, data } = img;
    const cells = new Int8Array(width * height);
    const mode = (p.mode ?? "trinary").toLowerCase();
    for (let y = 0; y < height; y++) {
        const dst = width * (height - y - 1);
        for (let x = 0; x < width; x++) {
            const v = data[y * width + x];
            let cell;
            if (mode === "raw")
                cell = v > 100 ? -1 : v;
            else {
                const shade = v / 255;
                const occ = p.negate ? shade : 1 - shade;
                if (mode === "scale")
                    cell = occ < p.freeThresh ? 0 : occ >= p.occupiedThresh ? 100 : Math.round(((occ - p.freeThresh) / (p.occupiedThresh - p.freeThresh)) * 100);
                else
                    cell = p.occupiedThresh < occ ? 100 : occ < p.freeThresh ? 0 : -1;
            }
            cells[dst + x] = cell;
        }
    }
    return cells;
}
/**
 * Arm controls, Phase 1 (docs/design-arm-teleop-and-3d-maps.md). Served on the robot by
 * tcr_platform_bridge arm_ops; the agent reports these capabilities when the matching
 * registry ids are in its local allowlist:
 *   ARM_STATE_CAPABILITY  ← arm.state          (observe)
 *   ARM_POSES_CAPABILITY  ← arm.go_to_pose     (+ arm.gripper / arm.tool_change / arm.stop as allowlisted)
 *
 * ROS types (package tcr_platform_interfaces) — normative for both sides:
 *
 *   msg/ArmState
 *     builtin_interfaces/Time stamp
 *     string model                # arm model id, e.g. "uf850" (the web picks its kinematic model by it)
 *     string[] joint_names        # arm joints in kinematic order (uf850_joint1..6)
 *     float64[] positions         # rad, same order as joint_names
 *     string tool                 # attached tool id ("" = none, "unknown" = cannot tell)
 *     string[] tools              # tool ids arm.tool_change accepts on this robot
 *     float64 gripper_position    # m (0 = closed); NaN when no gripper is attached
 *     string[] named_poses        # names arm.go_to_pose accepts on this robot
 *     string current_pose         # the named pose the arm is at (all joints within 0.02 rad), else ""
 *     bool moving                 # any arm goal is executing
 *     bool estopped               # software/hardware e-stop is holding the arm
 *     string active_goal          # "go_to_pose:<name>" | "change_tool:<tool>" | "gripper:<cmd>" | ""
 *     string fault                # "" when healthy, else a short reason (e.g. "controller inactive")
 *
 *   action/GoToNamedPose   goal {string name, float64 velocity_scaling (0 = robot default 0.2)}
 *                          result {bool success, string message}
 *                          feedback {string phase ("planning"|"executing"), float64 progress (0..1)}
 *   srv/ArmGripper         request {string command ("open"|"close")} → {bool success, string message}
 *   action/ChangeTool      goal {string tool} → result {bool success, string message}
 *                          feedback {string phase, float64 progress}
 *     # Phase 2a (arm jog); absent on robots without arm.jog.v1:
 *     bool jog_active             # a jog session is live (Servo unpaused, accepting ArmJog)
 *     string jog_status           # ARM_JOG_STATUSES: "ok"|"singularity"|"joint_limit"|"collision"|"paused"
 *     string jog_message          # human-readable detail of jog_status (e.g. Servo's status text), else ""
 *
 *   msg/ArmJog             (arm.jog, Phase 2a; published by the agent on /platform/arm/jog)
 *     std_msgs/Header header      # stamp = agent node clock (sim time in sim): Servo drops stale commands by stamp
 *     string frame                # "tool" | "base" (Cartesian jog frame; ignored for a joint jog)
 *     geometry_msgs/Twist twist   # m/s, rad/s; all zero for a joint jog
 *     string joint_name           # "" for a Cartesian jog
 *     float64 joint_velocity      # rad/s
 *     uint16 hold_ms              # apply for at most this long without a newer message (≤ 300)
 *
 *   arm.stop               std_srvs/srv/Trigger. success = stop CONFIRMED: every goal a downstream
 *                          server listed as cancelling (and every arm_ops goal) reached a terminal
 *                          status and the arm joints are stationary. success = false otherwise; the
 *                          message starts "arm stopped:", "arm stop requested, not confirmed:" or
 *                          "arm stop rejected:" and names each server's answer (a cancel request
 *                          answered is not evidence that motion stopped).
 *
 * Downstream goals stay tracked by arm_ops after a stop / cancel / timeout ends the platform goal:
 * a late acceptance is cancelled, and new arm motion is refused (ArmState.active_goal shows the
 * old goal, fault "downstream outcome of … not known yet") until its outcome is known.
 *
 * Modes: arm.go_to_pose / arm.gripper / arm.tool_change run in mode `teleop` (teleop lease).
 * Exception: the poses in ARM_RECOVERY_POSES also run in mode `recovery` under an `operate`
 * lease, so an operator can put the arm away after a failed mission without teleop; the
 * robot refuses any other pose in mode `recovery`. arm.stop is mode `safety`, no lease, and —
 * like e-stop ENGAGE — bypasses the authority-generation check (admitted with a stale or no
 * installed generation; the platform never holds or supersedes it): stopping is always safe. While an arm motion command runs the robot drops base teleop frames
 * (TeleopStatus stoppedReason `group_busy`) and refuses arm motion while base teleop moved
 * within the last 300 ms: one moving part at a time.
 *
 * Arm jog, Phase 2a (ADR-018; ARM_JOG_CAPABILITY ← arm.jog). Hold-to-move through MoveIt Servo:
 *  - The platform streams `arm_jog` envelopes (ArmJogFrameBody), never `cmd`s. The agent admits
 *    them like base teleop frames (enabled → teleop installation → authorityGeneration → lease →
 *    seq → e-stop → motion group → TELEOP/manual mode), clamps, and publishes ArmJog on
 *    /platform/arm/jog. arm_ops (which alone owns Servo) admits only in TELEOP/manual, not
 *    e-stopped, with no arm goal active, and keeps Servo paused outside a jog session.
 *  - The robot clamps to 0.10 m/s linear, 0.3 rad/s angular, 0.3 rad/s per joint whatever the
 *    platform sends (platform bounds: 0.05 / 0.3 / 0.3). A frame holds for min(holdMs, 300) ms;
 *    then the robot stops the arm (zero command, Servo paused, arm held on the gate).
 *  - Motion-group exclusivity both ways: base `teleop` frames are dropped while the arm jogs or an
 *    arm command runs; `arm_jog` is dropped while an arm command runs or the base moved within
 *    300 ms (teleop_status {group, stoppedReason: "group_busy"}). arm.go_to_pose / arm.tool_change
 *    are refused while ArmState.jog_active.
 *  - `teleop_stop` with group "arm" stops the jog only, "base" the base only, no group both.
 *    Always honoured, like base teleop stops.
 *  - Servo halts are reported as teleop_status {group: "arm", active: false, stoppedReason:
 *    "singularity" | "joint_limit" | "collision"} and in ArmState.jog_status / jog_message.
 *  - Leaving manual mode, e-stop and arm.stop pause Servo and hold the arm.
 * The backend only forwards `arm_jog` for a lease whose holder confirmed visibility of the arm
 * (POST /api/v1/robots/:id/arm/jog-session, D7); the robot cannot check this and does not.
 */
export const ARM_STATE_CAPABILITY = "arm.state.v1";
export const ARM_POSES_CAPABILITY = "arm.poses.v1";
export const ARM_JOG_CAPABILITY = "arm.jog.v1";
export const ARM_RECOVERY_POSES = ["tuck", "home"];
/** ArmState.jog_status values. */
export const ARM_JOG_STATUSES = ["ok", "singularity", "joint_limit", "collision", "paused"];
export const MapExportArgs = z.object({
    importId: z.string().uuid(),
    /** Platform robot-api URL for this import (POST, robot bearer token). */
    uploadUrl: z.string().url(),
    topic: z.string().min(1).max(256).default("/map"),
    waitSec: z.number().positive().max(60).default(10),
    maxBytes: z.number().int().positive().max(256 * 1024 * 1024).default(64 * 1024 * 1024),
});
/** Platform answer to the upload, returned unchanged as the map_export command result. */
export const MapExportResult = z.object({
    importId: z.string().uuid(),
    mapId: z.string().uuid(),
    mapVersion: z.number().int().positive(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    resolution: z.number().positive(),
});
const Sha256Hex = z.string().regex(/^[a-f0-9]{64}$/);
/** Bundle versions the robot must keep (pinned by unfinished missions, the active one). */
const RetainVersions = z.array(z.number().int().positive()).max(64).default([]);
export const ConfigStageArgs = z.object({
    deploymentId: z.string().uuid(),
    version: z.number().int().positive(),
    sha256: Sha256Hex,
    sizeBytes: z.number().int().positive(),
    /** HTTPS URL; robot authenticates with its bearer token. */
    url: z.string().url(),
    retainVersions: RetainVersions,
});
/** What the robot checked before staging (recorded on the deployment). */
export const CompatibilityReport = z.object({
    compatible: z.literal(true),
    /** Config API level the bundle requires (manifest.compat.configApi, 1 when absent). */
    configApi: z.number().int().positive(),
    softwareVersion: z.string().max(128),
    /** Content schemas the bundle uses, as accepted by the robot. */
    schemas: z.record(z.number().int().positive()),
    /** Robot capabilities the bundle requires (all present). */
    capabilities: z.array(z.string()).default([]),
});
export const ConfigStageResult = z.object({
    stagedVersion: z.number().int().positive(),
    sha256: Sha256Hex,
    compatibility: CompatibilityReport,
});
export const ConfigActivateArgs = z.object({
    deploymentId: z.string().uuid(),
    version: z.number().int().positive(),
    sha256: Sha256Hex,
    retainVersions: RetainVersions,
});
/** Active-version acknowledgement: what is active on the robot after the activation. */
export const ConfigActiveReport = z.object({
    activeVersion: z.number().int().positive(),
    activeSha256: Sha256Hex,
    deploymentId: z.string(),
    previousVersion: z.number().int().positive().nullable().default(null),
});
export const CmdBody = z.object({
    commandId: z.string().uuid(),
    op: CmdOp,
    /** Registry id. For config_stage / config_activate: WELL_KNOWN_INTERFACES.stageConfigBundle / activateConfigBundle. */
    interfaceId: z.string(),
    args: z.record(z.unknown()).default({}),
    /** Validity window measured from robot receipt. */
    ttlMs: z.number().int().positive().max(24 * 3600 * 1000),
    missionId: z.string().uuid().nullable().default(null),
    leaseId: z.string().uuid().nullable().default(null),
    /** Authority generation the command was issued under (see header). */
    authorityGeneration: z.number().int().nonnegative(),
    /** What the command does; must be permitted by the installed authority mode. */
    mode: CommandMode,
    /** Opaque payload identity (lowercase hex sha256); same commandId + different hash → CMD.PAYLOAD_MISMATCH. */
    payloadSha256: z.string().regex(/^[a-f0-9]{64}$/),
    /** Platform epoch-ms at dispatch (informational only). */
    issuedAt: z.number().int(),
});
/**
 * Install a new authority generation. The robot persists it, then answers
 * `authority_ack`. Idempotent: re-sending the installed generation re-acks it.
 * A generation below the installed one is refused (`accepted:false`, AUTH.STALE_GENERATION).
 */
export const AuthorityInstallBody = z.object({
    generation: z.number().int().positive(),
    mode: ControlMode,
    /** Lease the generation belongs to; null when nobody holds control (lease-less work allowed in `operate`). */
    leaseId: z.string().uuid().nullable(),
    ownerKind: z.enum(["user", "api_token", "none"]),
    /** What happens to admitted work. Only `continue` is defined: it keeps running. */
    disposition: z.literal("continue"),
});
/**
 * Cancellation request. Always honoured (stopping is never gated on authority).
 * The robot reports `cmd_event cancel_accepted` / `cancel_rejected` (v0.3 §7.7), and
 * the command's real outcome as cmd_result (`cancelled`, or whatever it finished
 * with). Legacy robots signal a refusal with `cmd_feedback` carrying
 * `feedback: { cancel: { accepted: false, error: LinkError } }` (CancelRefusal);
 * the platform still accepts that shape.
 */
export const CmdCancelBody = z.object({ commandId: z.string().uuid(), reason: z.string().max(500).default("") });
/** Shape of the cancel-refusal marker inside cmd_feedback.feedback. */
export const CancelRefusal = z.object({ cancel: z.object({ accepted: z.literal(false), error: LinkError }) });
/**
 * Base teleop frame (body frame, SI units). The agent MUST:
 *  - drop frames whose seq <= last applied seq for the lease,
 *  - clamp to its own configured limits,
 *  - publish zero velocity if no newer frame arrives within holdMs.
 */
export const TeleopFrameBody = z.object({
    leaseId: z.string().uuid(),
    /** Must equal the installed generation under a `teleop` installation; otherwise the frame is dropped. */
    authorityGeneration: z.number().int().positive(),
    seq: z.number().int().nonnegative(),
    vx: z.number().finite(),
    vy: z.number().finite(),
    wz: z.number().finite(),
    /** Max time the robot may keep applying this frame. */
    holdMs: z.number().int().min(50).max(500),
});
/** Cartesian jog velocity in the jog frame: m/s, rad/s. */
export const ArmJogTwist = z.object({
    vx: z.number().finite(),
    vy: z.number().finite(),
    vz: z.number().finite(),
    wx: z.number().finite(),
    wy: z.number().finite(),
    wz: z.number().finite(),
});
/** Single-joint jog: joint name as in ArmState.joint_names, velocity in rad/s. */
export const ArmJogJoint = z.object({ name: z.string().min(1), velocity: z.number().finite() });
/**
 * Arm jog frame (Phase 2a, ADR-018; rules above ARM_JOG_CAPABILITY). Same lease / generation /
 * seq / hold rules as TeleopFrameBody. Exactly one of `twist` (Cartesian, in `frame`) or
 * `joint` (single joint; `frame` is ignored).
 */
export const ArmJogFrameBody = z
    .object({
    leaseId: z.string().uuid(),
    authorityGeneration: z.number().int().positive(),
    seq: z.number().int().nonnegative(),
    frame: z.enum(["tool", "base"]),
    twist: ArmJogTwist.optional(),
    joint: ArmJogJoint.optional(),
    /** Max time the robot may keep applying this frame. */
    holdMs: z.number().int().min(50).max(300),
})
    .refine((b) => (b.twist === undefined) !== (b.joint === undefined), { message: "exactly one of twist or joint", path: ["twist"] });
/**
 * Always honoured (stopping is never gated on authority); `authorityGeneration` is informational.
 * `group`: stop only that stream; absent = stop base and arm (older robots ignore the field and stop the base).
 */
export const TeleopStopBody = z.object({
    leaseId: z.string().uuid(),
    authorityGeneration: z.number().int().nonnegative().optional(),
    group: TeleopGroup.optional(),
    reason: z.string().max(200),
});
export const ReconcileDoneBody = z.object({
    /** Commands the platform marked `unknown` (outcome not established) because the robot had no record of them. */
    unknownCommandIds: z.array(z.string().uuid()),
});
export const PingBody = z.object({ nonce: z.string() });
export const LinkErrorBody = LinkError;
// ───────────────────────────── envelope ─────────────────────────────
const env = (type, body) => z.object({
    v: z.literal(ROBOT_LINK_PROTOCOL_VERSION),
    type: z.literal(type),
    /** Unique per frame (UUID); used for logging/correlation only. */
    id: z.string().min(1).max(64),
    /** Sender epoch-ms. */
    ts: z.number().int(),
    /**
     * Robot→platform only: strictly increasing per link connection, 0 on `hello`.
     * Optional in the schema so platform→robot frames need not carry it.
     */
    seq: z.number().int().nonnegative().optional(),
    body,
});
export const RobotToPlatform = z.discriminatedUnion("type", [
    env("hello", HelloBody),
    env("state", StateBody),
    env("telemetry", TelemetryBody),
    env("cmd_ack", CmdAckBody),
    env("cmd_feedback", CmdFeedbackBody),
    env("cmd_result", CmdResultBody),
    env("cmd_event", CmdEventBody),
    env("authority_ack", AuthorityAckBody),
    env("bind_report", BindReportBody),
    env("teleop_status", TeleopStatusBody),
    env("pong", PongBody),
    env("auth_refresh", AuthRefreshBody),
]);
export const PlatformToRobot = z.discriminatedUnion("type", [
    env("welcome", WelcomeBody),
    env("registry_update", RegistrySnapshot),
    env("cmd", CmdBody),
    env("cmd_cancel", CmdCancelBody),
    env("authority_install", AuthorityInstallBody),
    env("teleop", TeleopFrameBody),
    env("teleop_stop", TeleopStopBody),
    env("arm_jog", ArmJogFrameBody),
    env("reconcile_done", ReconcileDoneBody),
    env("ping", PingBody),
    env("error", LinkErrorBody),
]);
/** Helper to build an envelope with a fresh id/timestamp. */
export function envelope(type, body, now = Date.now()) {
    return { v: ROBOT_LINK_PROTOCOL_VERSION, type, id: globalThis.crypto.randomUUID(), ts: now, body };
}
/** Calibration helper: maps a calibration type to its registry id. */
export function calibrationInterfaceId(t) {
    return `calibration.${t}`;
}
/**
 * Arguments for a calibration command, shaped for the registry entry's ROS interface:
 *  - action (tcr_platform_interfaces/action/Calibrate): `calibration_type` + `params_json`.
 *    `confirm_clear: true` records that the requesting user confirmed the workspace is
 *    clear (the platform only sends it after the UI confirmation); robot-side adapters
 *    for routines that move hardware refuse goals without it.
 *  - service (std_srvs/Trigger-style): no request fields.
 */
export function calibrationArgs(kind, t) {
    if (kind === "action")
        return { calibration_type: t, params_json: JSON.stringify({ confirm_clear: true }) };
    return {};
}
/** WebSocket close codes used on the robot link (4000-4999 = application). */
export const LINK_CLOSE = {
    normal: 1000,
    goingAway: 1001,
    protocolError: 4000,
    unauthorized: 4001,
    authExpired: 4002,
    superseded: 4003, // a newer connection for the same robot replaced this one
    revoked: 4004,
    helloTimeout: 4005,
    heartbeatTimeout: 4006,
};
/**
 * Request/goal fields of the ROS types the platform builds arguments for (verified
 * against the .srv/.action files in the tcr repo). The onboard agent fills messages
 * with rosidl set_message_fields, which REJECTS unknown fields, so platform-built args
 * must stay within these. The mock robot validates against this table to catch drift.
 * Add an entry whenever the platform starts constructing args for another type.
 */
export const ROS_REQUEST_FIELDS = {
    "std_srvs/srv/Trigger": [],
    "monitoring/srv/SwEstopTrig": ["requester", "hold"],
    "monitoring/srv/ResolveError": ["error_id"],
    "robot_interfaces/srv/TeleopCommand": ["command", "params_json"],
    "tcr_platform_interfaces/action/RunWorkflow": ["mission_id", "workflow_id", "workflow_version", "bundle_version", "bundle_sha256", "params_json"],
    "tcr_platform_interfaces/action/Calibrate": ["calibration_type", "params_json"],
    "tcr_platform_interfaces/srv/StageBundle": ["deployment_id", "version", "bundle_dir", "sha256", "retain_versions"],
    "tcr_platform_interfaces/srv/ActivateBundle": ["deployment_id", "version", "sha256", "retain_versions"],
    "tcr_platform_interfaces/action/GoToNamedPose": ["name", "velocity_scaling"],
    "tcr_platform_interfaces/srv/ArmGripper": ["command"],
    "tcr_platform_interfaces/action/ChangeTool": ["tool"],
};
/** Fields in `args` that the ROS type does not have (empty when the type is not in the table). */
export function unknownRosFields(rosType, args) {
    const fields = ROS_REQUEST_FIELDS[rosType];
    if (!fields)
        return [];
    return Object.keys(args).filter((k) => !fields.includes(k));
}
//# sourceMappingURL=robot-link.js.map